<template>
  <div class="section-style-panel mt-3">
    <div class="section-style-panel__head">
      <span class="section-style-panel__title">
        <md-icon class="mr-1" style="font-size: 18px">palette</md-icon> Section Style
      </span>
      <b-btn
        v-if="hasOverrides"
        variant="outline-danger"
        size="sm"
        class="section-style-panel__reset"
        @click.prevent="reset"
      >
        <i class="fas fa-undo mr-1"></i> Reset
      </b-btn>
    </div>

    <small class="d-block mb-3 text-muted">
      Customize this section's styling using your brand palette. Reset clears overrides and falls back to theme
      defaults.
    </small>

    <!-- Text Alignment (Left / Center only) -->
    <b-form-group v-if="capabilities.showAlignment" label="Text Alignment" label-class="form-label">
      <div class="row" style="margin-left: -10px">
        <div
          v-for="option in alignOptions"
          :key="option.value"
          class="clickable-item py-2 mx-2"
          @click.prevent="update('textAlign', option.value)"
        >
          <div class="card" :class="value.textAlign === option.value ? 'border-outline-primary-btn' : ''">
            <md-icon
              v-b-popover.hover.right
              :title="option.text"
              style="font-size: 35px"
              :class="value.textAlign === option.value ? 'text-primary' : ''"
            >{{ option.icon }}</md-icon>
          </div>
        </div>
      </div>
    </b-form-group>

    <!-- Section Badge / Eyebrow -->
    <div class="section-style-panel__subhead">Section Badge</div>
    <b-form-group label="Badge Text" label-class="form-label">
      <b-form-input
        class="form-control"
        :value="badge.text"
        placeholder="e.g. Now Enrolling (leave blank for the default)"
        @input="updateBadge('text', $event)"
      />
    </b-form-group>

    <template v-if="organization">
      <b-form-group label="Badge Text Color" label-class="form-label">
        <div class="d-flex flex-wrap">
          <div
            v-for="opt in badgeTextColorOpts"
            :key="opt.value"
            class="swatch"
            :style="{ background: badgeColorMap[opt.value] }"
            :class="badge.textColor === opt.value ? 'swatch--active' : ''"
            @click.prevent="updateBadge('textColor', opt.value)"
          >
            <md-icon v-if="badge.textColor === opt.value" class="swatch__check m-auto" :class="checkClass(opt.value)">check</md-icon>
          </div>
        </div>
        <small class="text-muted">Falls back to your brand/primary color.</small>
      </b-form-group>

      <b-form-group label="Badge Background" label-class="form-label">
        <div class="d-flex flex-wrap">
          <!-- None = plain eyebrow text (no pill) -->
          <div
            class="swatch swatch--none"
            :class="!badge.backgroundColor ? 'swatch--active' : ''"
            @click.prevent="updateBadge('backgroundColor', '')"
          >
            <md-icon class="m-auto text-muted" style="font-size: 20px">block</md-icon>
          </div>
          <div
            v-for="opt in badgeBackgroundOpts"
            :key="opt.value"
            class="swatch"
            :style="{ background: badgeColorMap[opt.value] }"
            :class="badge.backgroundColor === opt.value ? 'swatch--active' : ''"
            @click.prevent="updateBadge('backgroundColor', opt.value)"
          >
            <md-icon v-if="badge.backgroundColor === opt.value" class="swatch__check m-auto" :class="bgCheckClass(opt.value)">check</md-icon>
          </div>
        </div>
        <small class="text-muted">Choose a color for a pill, or "None" for plain text.</small>
      </b-form-group>
    </template>

    <!-- Text Color (White / Black) -->
    <b-form-group v-if="capabilities.showText && organization" label="Text Color" label-class="form-label">
      <div class="d-flex flex-wrap">
        <div
          v-for="opt in textColorOpts"
          :key="opt"
          class="swatch"
          :style="{ background: opt }"
          :class="value.textColor === opt ? 'swatch--active' : ''"
          @click.prevent="update('textColor', opt)"
        >
          <md-icon v-if="value.textColor === opt" class="swatch__check m-auto" :class="checkClass(opt)">check</md-icon>
        </div>
      </div>
    </b-form-group>

    <!-- Button Color -->
    <b-form-group v-if="capabilities.showButton && organization" label="Button Color" label-class="form-label">
      <div class="d-flex flex-wrap align-items-center">
        <div
          v-for="opt in btnColorOpts"
          :key="opt"
          class="swatch"
          :style="{ background: opt }"
          :class="value.btnColor === opt ? 'swatch--active' : ''"
          @click.prevent="update('btnColor', opt)"
        >
          <md-icon v-if="value.btnColor === opt" class="swatch__check m-auto" :class="checkClass(opt)">check</md-icon>
        </div>
      </div>
    </b-form-group>

    <!-- Background Color -->
    <b-form-group v-if="capabilities.showBackground && organization" label="Background Color" label-class="form-label">
      <div class="d-flex flex-wrap">
        <div
          v-for="opt in backgroundColorOpts"
          :key="opt.value"
          class="swatch"
          :style="{ background: backgroundColorMap[opt.value] }"
          :class="value.backgroundColor === opt.value ? 'swatch--active' : ''"
          @click.prevent="update('backgroundColor', opt.value)"
        >
          <md-icon v-if="value.backgroundColor === opt.value" class="swatch__check m-auto" :class="bgCheckClass(opt.value)">check</md-icon>
        </div>
      </div>
    </b-form-group>

    <!-- Card controls -->
    <template v-if="capabilities.showCard && organization">
      <div class="section-style-panel__subhead">Card Style</div>

      <b-form-group label="Card Background" label-class="form-label">
        <div class="d-flex flex-wrap">
          <div
            v-for="opt in backgroundColorOpts"
            :key="opt.value"
            class="swatch"
            :style="{ background: backgroundColorMap[opt.value] }"
            :class="value.cardBackgroundColor === opt.value ? 'swatch--active' : ''"
            @click.prevent="update('cardBackgroundColor', opt.value)"
          >
            <md-icon v-if="value.cardBackgroundColor === opt.value" class="swatch__check m-auto" :class="bgCheckClass(opt.value)">check</md-icon>
          </div>
        </div>
      </b-form-group>

      <b-form-group label="Card Text Color" label-class="form-label">
        <div class="d-flex flex-wrap">
          <div
            v-for="opt in textColorOpts"
            :key="opt"
            class="swatch"
            :style="{ background: opt }"
            :class="value.cardTextColor === opt ? 'swatch--active' : ''"
            @click.prevent="update('cardTextColor', opt)"
          >
            <md-icon v-if="value.cardTextColor === opt" class="swatch__check m-auto" :class="checkClass(opt)">check</md-icon>
          </div>
        </div>
      </b-form-group>

      <b-form-group v-if="capabilities.showButton" label="Card Button Color" label-class="form-label">
        <div class="d-flex flex-wrap align-items-center">
          <div
            v-for="opt in btnColorOpts"
            :key="opt"
            class="swatch"
            :style="{ background: opt }"
            :class="value.cardBtnColor === opt ? 'swatch--active' : ''"
            @click.prevent="update('cardBtnColor', opt)"
          >
            <md-icon v-if="value.cardBtnColor === opt" class="swatch__check m-auto" :class="checkClass(opt)">check</md-icon>
          </div>
        </div>
      </b-form-group>
    </template>
  </div>
</template>

<script>
export const BADGE_DEFAULTS = {
  text: '',
  textColor: '',
  backgroundColor: '',
};

export const SECTION_STYLE_DEFAULTS = {
  textAlign: 'left',
  textColor: '',
  btnColor: '',
  backgroundColor: '',
  cardBackgroundColor: '',
  cardTextColor: '',
  cardBtnColor: '',
  badge: { ...BADGE_DEFAULTS },
};

export default {
  name: 'SectionStylePanel',
  props: {
    // The section style object stored under homeLayout[sectionKey]
    value: {
      type: Object,
      required: true,
    },
    capabilities: {
      type: Object,
      required: true,
    },
    // Selected school / school's linked entity, exposing brand palette colors
    organization: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      alignOptions: [
        { text: 'Left', icon: 'format_align_left', value: 'left' },
        { text: 'Center', icon: 'format_align_center', value: 'center' },
      ],
      textColorOpts: ['white', 'black'],
      // Badge colors are stored as theme keys the public site resolves to CSS vars.
      badgeTextColorOpts: [
        { text: 'Primary', value: 'primary' },
        { text: 'Secondary', value: 'secondary' },
        { text: 'White', value: 'white' },
        { text: 'Black', value: 'black' },
      ],
      badgeBackgroundOpts: [
        { text: 'Primary', value: 'primary' },
        { text: 'Secondary', value: 'secondary' },
        { text: 'Light', value: 'light' },
        { text: 'Dark', value: 'dark' },
        { text: 'White', value: 'white' },
      ],
      backgroundColorOpts: [
        { text: 'Light', value: 'light' },
        { text: 'Primary', value: 'primary' },
        { text: 'Dark', value: 'dark' },
        { text: 'Secondary', value: 'secondary' },
        { text: 'White', value: 'white' },
        { text: 'Gradient', value: 'gradient' },
      ],
    };
  },
  computed: {
    backgroundColorMap() {
      if (this.organization) {
        return {
          light: `${this.organization.primary_color}15`,
          primary: this.organization.primary_color,
          dark: this.organization.dark_color,
          secondary: this.organization.secondary_color,
          white: '#fff',
          gradient: `linear-gradient(to bottom, ${this.organization.primary_color}15, ${this.organization.primary_color})`,
        };
      }
      return {};
    },
    // Preview colors for badge swatches (theme key -> brand color).
    badgeColorMap() {
      if (this.organization) {
        return {
          primary: this.organization.primary_color,
          secondary: this.organization.secondary_color,
          light: `${this.organization.primary_color}15`,
          dark: this.organization.dark_color,
          white: '#fff',
          black: '#000',
        };
      }
      return {};
    },
    btnColorOpts() {
      if (this.organization) {
        return [
          `${this.organization.primary_color}15`,
          this.organization.primary_color,
          this.organization.dark_color,
          this.organization.secondary_color,
          '#fff',
        ];
      }
      return [];
    },
    // Badge/eyebrow config, always a full object even for legacy rows.
    badge() {
      return { ...BADGE_DEFAULTS, ...(this.value.badge || {}) };
    },
    hasOverrides() {
      // True when any scalar control differs from the section-style defaults...
      const scalarChanged = Object.keys(SECTION_STYLE_DEFAULTS)
        .filter(key => key !== 'badge')
        .some(key => (this.value[key] || '') !== SECTION_STYLE_DEFAULTS[key]);
      // ...or any badge field has been set.
      const badgeChanged = Object.keys(BADGE_DEFAULTS).some(key => (this.badge[key] || '') !== BADGE_DEFAULTS[key]);
      return scalarChanged || badgeChanged;
    },
  },
  methods: {
    update(key, val) {
      this.$emit('input', { ...this.value, [key]: val });
    },
    updateBadge(key, val) {
      this.$emit('input', { ...this.value, badge: { ...this.badge, [key]: val } });
    },
    reset() {
      this.$emit('input', { ...SECTION_STYLE_DEFAULTS, badge: { ...BADGE_DEFAULTS } });
    },
    // Pick a readable check color for white/black text swatches.
    checkClass(opt) {
      return opt === 'white' ? 'text-dark' : 'text-white';
    },
    // For background swatches: light/white backgrounds get a dark check, others a white check.
    bgCheckClass(value) {
      return value === 'light' || value === 'white' ? 'text-dark' : 'text-white';
    },
  },
};
</script>

<style lang="css" scoped>
.section-style-panel {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  background: #fafbfc;
  padding: 16px;
  margin-bottom: 24px;
}

.section-style-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.section-style-panel__title {
  display: inline-flex;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
  color: #313340;
}

.section-style-panel__reset {
  border-radius: 20px;
  padding: 2px 12px;
  font-size: 12px;
}

.section-style-panel__subhead {
  font-weight: 600;
  font-size: 13px;
  color: #5b5d6b;
  margin: 14px 0 8px;
  padding-top: 12px;
  border-top: 1px dashed #e1e4e8;
}

/* Color swatches */
.swatch {
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid #cdd1d6;
  margin-right: 12px;
  margin-bottom: 6px;
  cursor: pointer;
  display: flex;
  transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease;
}

.swatch:hover {
  transform: scale(1.06);
  border-color: #9aa0a6;
}

.swatch--none {
  background: repeating-linear-gradient(45deg, #fff, #fff 5px, #f0f0f0 5px, #f0f0f0 10px);
}

.swatch--active {
  border: 2px solid var(--secondary-color, #1b68ff) !important;
  box-shadow: 0 0 0 3px rgba(27, 104, 255, 0.18);
  transform: scale(1.08);
}

.swatch__check {
  font-size: 20px;
  font-weight: 700;
}
</style>
